import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Container, Alert, Box, Button, TextField, FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery, FormControlLabel, Checkbox, CircularProgress, Typography, IconButton, Switch, Paper, List, ListItemButton, ListItemText } from "@mui/material";
import { MenuInicial } from "./MenuInicial";
import Cargando from './utils/Cargando.jsx';
import { Save as SaveIcon, PersonOutline, Visibility, VisibilityOff, Refresh, ContentCopy, CheckCircle } from '@mui/icons-material';
import TituloPagina from './utils/TituloPagina.jsx';
import VolverBreadcrumb from './utils/VolverBreadcrumb.jsx';
import { API_BASE_URL, USERACTUALIZAR, USUARIOALTA, USUARIOGET, USUARIOFORMDATA } from '../services/apiConfig';
import { avisarExito, avisarError } from '../services/toast';
import axios from "axios";
import './estilos.css';

function User() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userData = JSON.parse(sessionStorage.getItem("userData") || '{}');
  const usuarioidFromSession = userData?.usuarioid;
  const usuarioidFromUrl = searchParams.get('userid');
  const usuarioid = usuarioidFromUrl || usuarioidFromSession;
  const isCreateMode = searchParams.get('mode') === 'create';
  const esAutoedicion = !isCreateMode && Number(usuarioid) === Number(usuarioidFromSession);
  const perfilLogueado = Number(userData?.perfil);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [error, setError] = useState('');

  // Estados para los nuevos campos
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [auditarOperaciones, setAuditarOperaciones] = useState(false);
  const [activo, setActivo] = useState(true);
  const [bloqueado, setBloqueado] = useState(false);
  const [perfilId, setPerfilId] = useState('');
  const [perfiles, setPerfiles] = useState([]);
  const [sucursalesDisponibles, setSucursalesDisponibles] = useState([]);
  const [sucursalesAsignadas, setSucursalesAsignadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [credencialesCreadas, setCredencialesCreadas] = useState(null); // {texto} tras crear, cuando corresponde ofrecer copiarlas
  const [copiado, setCopiado] = useState(false);

  // Sin caracteres ambiguos (0/O, 1/l/I) para que se pueda transcribir a mano sin dudas.
  const generarPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const random = new Uint32Array(12);
    window.crypto.getRandomValues(random);
    setPassword(Array.from(random, (n) => chars[n % chars.length]).join(''));
    setShowPassword(true);
  };

  const copiarCredenciales = () => {
    if (!credencialesCreadas) return;
    navigator.clipboard.writeText(credencialesCreadas.texto).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  useEffect(() => {
    if (isCreateMode) {
      setLoading(false);
    } else {
      loadUserData();
    }
    loadFormData();
  }, []);

  const loadFormData = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await axios.get(`${API_BASE_URL}${USUARIOFORMDATA}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        const payload = response.data?.data ?? response.data;
        setPerfiles(payload?.perfiles ?? response.data.perfiles ?? []);
        setSucursalesDisponibles(payload?.sucursales ?? []);
      }
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  };

  const loadUserData = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await axios.get(`${API_BASE_URL}${USUARIOGET}?usuarioid=${usuarioid}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        const user = response.data.data.usuario;
        setName(user.username || '');
        setEmail(user.email || '');
        setAuditarOperaciones(user.auditaroperaciones == 1);
        setActivo(Number(user.activo) === 1);
        setBloqueado(Number(user.bloqueado) === 1);
        setPerfilId(user.perfil || '3');
        setSucursalesAsignadas((response.data.data.sucursales || []).map(Number));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Perfil 3 (empleado) = una sola sucursal (el backend igual lo fuerza, esto
  // solo lo hace explícito en la UI, como un radio); perfil 1/2, múltiples.
  const toggleSucursal = (id) => {
    setSucursalesAsignadas((prev) => {
      const esEmpleado = String(perfilId) === '3';
      const yaEsta = prev.includes(id);
      if (esEmpleado) return yaEsta ? [] : [id];
      return yaEsta ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = sessionStorage.getItem("authToken");

      if (isCreateMode) {
        if (!password.trim()) {
          setError('La contraseña es requerida');
          setSubmitting(false);
          return;
        }
        const payload = {
          username: name,
          email: email,
          password: password,
          perfil: perfilId,
          auditaroperaciones: auditarOperaciones ? 1 : 0,
          sucursales: sucursalesAsignadas,
        };
        const response = await axios.post(`${API_BASE_URL}${USUARIOALTA}`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.data.success) throw new Error(response.data.message || 'Error al crear el usuario.');
        avisarExito(response.data.message || 'Usuario creado.');

        // Solo perfil 1 (Administrador) ve la opción de copiar credenciales, y
        // nunca para un usuario que él mismo creó como perfil 1 (no tiene
        // sentido difundir credenciales de otro admin así).
        const perfilNombre = perfiles.find((p) => String(p.id) === String(perfilId))?.descripcion || '';
        if (perfilLogueado === 1 && Number(perfilId) !== 1) {
          setCredencialesCreadas({
            texto: `Usuario: ${email}\nContraseña: ${password}\nPerfil: ${perfilNombre}`,
          });
          setSubmitting(false);
        } else {
          setTimeout(() => navigate('/userList'), 900);
        }
      } else {
        const payload = {
          usuarioid: usuarioid,
          email: email,
          auditaroperaciones: auditarOperaciones ? 1 : 0,
          perfil_id: perfilId,
        };
        if (password.trim()) {
          payload.password = password;
        }
        // Nunca en autoedición: el backend igual las ignora en ese caso, pero
        // ni se mandan — no te podés desactivar/bloquear a vos mismo.
        if (!esAutoedicion) {
          payload.activo = activo ? 1 : 0;
          payload.bloqueado = bloqueado ? 1 : 0;
          payload.sucursales = sucursalesAsignadas;
        }
        const response = await axios.post(`${API_BASE_URL}${USERACTUALIZAR}`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.data.success) throw new Error(response.data.message || 'Error al actualizar el usuario.');
        avisarExito(response.data.message || 'Usuario actualizado.');
        setTimeout(() => navigate(esAutoedicion ? '/dashboard' : '/userList'), 900);
      }
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al procesar.');
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth={false} sx={{ px: { xs: 1, md: 3 } }}>
        <MenuInicial />
        <Cargando inline />
      </Container>
    );
  }

  // Crear usuarios es solo para Administrador — si alguien más llega acá
  // (URL a mano), lo mandamos de vuelta antes de mostrar el formulario.
  if (isCreateMode && perfilLogueado !== 1) {
    return <Navigate to="/userList" replace />;
  }

  if (credencialesCreadas) {
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial />
        <TituloPagina icon={PersonOutline} titulo="Usuario creado" />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Paper variant="outlined" sx={{ p: 3, maxWidth: 480, width: '100%' }}>
            <Alert severity="success" sx={{ mb: 2 }}>Usuario creado correctamente.</Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Copiá estos datos para enviárselos — la contraseña no se puede volver a ver después de esto.
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: 'action.hover' }}>
              {credencialesCreadas.texto}
            </Paper>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => navigate('/userList')}>Volver a Usuarios</Button>
              <Button
                variant="contained"
                startIcon={copiado ? <CheckCircle /> : <ContentCopy />}
                onClick={copiarCredenciales}
              >
                {copiado ? 'Copiado' : 'Copiar'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Cruza sucursalesAsignadas (ids reales del usuario) con sucursalesDisponibles
  // (nombres) para mostrar TODAS las sucursales cuando tiene más de una — el
  // sucursalid=999 del token es solo un centinela de "no hay una sola" y no
  // alcanza para listar varias. Para perfil 3 (empleado), sucursalesDisponibles
  // siempre viene vacío del backend, así que ahí se cae al dato simple del token.
  const nombresSucursalesPropias = sucursalesDisponibles
    .filter((s) => sucursalesAsignadas.includes(Number(s.id)))
    .map((s) => s.nombre);
  const sucursalTexto = nombresSucursalesPropias.length > 0
    ? nombresSucursalesPropias.join(', ')
    : (userData?.sucursalid && Number(userData.sucursalid) !== 999 ? (userData?.sucursalnombre || '—') : 'Ninguna');

  return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
        <TituloPagina
          icon={PersonOutline}
          titulo={isCreateMode ? "Nuevo Usuario" : esAutoedicion ? "Mi perfil" : "Editar Usuario"}
          ayuda={esAutoedicion
            ? "Tus datos. Dejá la contraseña vacía si no querés cambiarla."
            : "El perfil define qué puede ver y hacer: Administrador (todo), Franquiciado (sus sucursales), Empleado (solo Turnos y Registrar en su sucursal)."}
        />
        <VolverBreadcrumb to={esAutoedicion ? "/dashboard" : "/userList"} label={esAutoedicion ? "Inicio" : "Usuarios"} />
        {error && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><Alert severity="error" sx={{ width: '100%', maxWidth: 400 }}>{error}</Alert></Box>}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Nombre de usuario"
            variant="outlined"
            fullWidth
            autoComplete="off"
            value={name}
            disabled={!isCreateMode}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />

          {esAutoedicion && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Perfil: {userData?.perfil_nombre || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sucursal{nombresSucursalesPropias.length > 1 ? 'es' : ''}: {sucursalTexto}
              </Typography>
            </Box>
          )}

          <TextField
            label={isCreateMode ? "Contraseña" : "Nueva Contraseña (dejar vacío para no cambiar)"}
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <Box sx={{ display: 'flex' }}>
                  {isCreateMode && (
                    <IconButton onClick={generarPassword} edge="end" size="small" title="Generar contraseña aleatoria">
                      <Refresh fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </Box>
              ),
            }}
          />

          {!esAutoedicion && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Perfil</InputLabel>
              <Select
                value={perfilId}
                label="Perfil"
                onChange={(e) => setPerfilId(e.target.value)}
              >
                {perfiles.map((perfil) => (
                  <MenuItem key={perfil.id} value={perfil.id}>
                    {perfil.descripcion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!esAutoedicion && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={auditarOperaciones}
                  onChange={(e) => setAuditarOperaciones(e.target.checked)}
                />
              }
              label="Auditar operaciones"
              sx={{ mb: 2, display: 'block' }}
            />
          )}

          {!isCreateMode && !esAutoedicion && (
            <>
              <FormControlLabel
                control={<Switch checked={activo} onChange={(e) => setActivo(e.target.checked)} />}
                label="Activo"
                sx={{ mb: 1, display: 'block' }}
              />
              <FormControlLabel
                control={<Switch checked={bloqueado} onChange={(e) => setBloqueado(e.target.checked)} color="error" />}
                label="Bloqueado"
                sx={{ mb: 2, display: 'block' }}
              />
              {bloqueado && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Bloqueado por intentos fallidos de contraseña. Desmarcá "Bloqueado" y guardá para desbloquearlo (también reinicia el contador de intentos).
                </Alert>
              )}
            </>
          )}

          {!esAutoedicion && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Sucursales{String(perfilId) === '3' ? ' (elegí una)' : ''}
              </Typography>
              {sucursalesDisponibles.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No tenés sucursales propias para asignar.</Typography>
              ) : (
                <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                  <List dense disablePadding>
                    {sucursalesDisponibles.map((s) => {
                      const elegida = sucursalesAsignadas.includes(Number(s.id));
                      return (
                        <ListItemButton key={s.id} onClick={() => toggleSucursal(Number(s.id))} dense selected={elegida}>
                          <Checkbox edge="start" checked={elegida} tabIndex={-1} disableRipple size="small" sx={{ mr: 1 }} />
                          <ListItemText primary={s.nombre} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </Box>
          )}

          <div className="miDivSep">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            >
              {submitting ? 'Guardando...' : isCreateMode ? 'Crear Usuario' : 'Actualizar'}
            </Button>
          </div>
        </form>
      </Box>

    </Container>
  );
}
 
export default User;
